import React, { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useThemeColors, getPrimaryButtonStyles } from '../hooks/useThemeColors';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAbortableLoad, isCanceled } from '../hooks/useAbortableLoad';
import {
  Settings, Loader2, Copy, RefreshCw, Eye, EyeOff, BookOpen, KeyRound, Plug, ShieldAlert, MessageCircle, Save, Download,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api/experiencia/encuestas`;

function construirMdEncuestas(apiUrl) {
  return `# Guía: API de Ingesta de Encuestas (NPS/CSAT)

Ingesta respuestas de encuestas EPA (NPS/CSAT) multicanal (TELEFONO, WHATSAPP y otros).

## Endpoint
\`\`\`
POST ${apiUrl}
\`\`\`

## Autenticación
\`\`\`
Authorization: Bearer <api_key>
Content-Type: application/json
\`\`\`

## Cuerpo (JSON)
- \`codigo_canal\` — **obligatorio**. "TELEFONO", "WHATSAPP" u otro código de canal registrado.
- \`codigo_punto_contacto\` — **opcional**. Si se omite, se deduce del \`codigo_canal\`.
- \`codigo_ticket\` — **opcional**\\*\\*. N° de ticket (ej. TCK-2026-00049).
- \`codigo_proyecto\` — **opcional**.
- \`documento\` / \`email\` / \`celular\` — **opcional**\\*\\*. Identificadores del cliente.
- \`nps\` — **opcional**\\* (0–10).
- \`csat\` — **opcional**\\* (0–5).
- \`comentario\`, \`area_funcional\`, \`codigo_etapa\`, \`fecha\` (ISO) — **opcional**.

\\* Debe enviarse al menos uno de \`nps\` o \`csat\`.
\\*\\* Regla de identificación: debe enviarse \`codigo_ticket\` **o** al menos un identificador (documento/email/celular). Si va solo \`codigo_ticket\`, el cliente se resuelve desde el ticket (Cliente 360).

## Ejemplo A — por identificador (cURL)
\`\`\`
curl -X POST "${apiUrl}" \\
  -H "Authorization: Bearer <api_key>" -H "Content-Type: application/json" \\
  -d '{
    "codigo_canal": "WHATSAPP",
    "codigo_punto_contacto": "SAC_TICKETS",
    "codigo_ticket": "TCK-2026-00049",
    "codigo_proyecto": "PAA",
    "documento": "72738721",
    "email": "cliente@dominio.com",
    "celular": "+51987654321",
    "nps": 9,
    "csat": 4,
    "comentario": "Atención rápida",
    "area_funcional": "Postventa"
  }'
\`\`\`

## Ejemplo B — canal TELEFONO, solo por codigo_ticket (cURL)
\`\`\`
curl -X POST "${apiUrl}" \\
  -H "Authorization: Bearer <api_key>" -H "Content-Type: application/json" \\
  -d '{
    "codigo_canal": "TELEFONO",
    "codigo_ticket": "TCK-2026-00049",
    "nps": 10,
    "csat": 5,
    "comentario": "Muy satisfecha con la atención"
  }'
\`\`\`

## Respuesta 200
\`\`\`json
{
  "ok": true,
  "respuesta_id": "uuid",
  "cliente_id": "uuid | null",
  "punto_contacto_resuelto": true,
  "match": "actualizada | insertada"
}
\`\`\`

## Errores
- \`400\` — nps fuera de 0–10 / csat fuera de 0–5.
- \`400\` — Debe enviar codigo_ticket o al menos un identificador (documento/email/celular).
- \`401\` — Token inválido o ausente.
`;
}


const CXConfiguracionPage = () => {
  const { api } = useAuth();
  const themeColors = useThemeColors();

  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState('');
  const [reveal, setReveal] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  // Config Encuesta NPS por WhatsApp
  const [waActivo, setWaActivo] = useState(false);
  const [waUrl, setWaUrl] = useState('');
  const [waApiKey, setWaApiKey] = useState('');       // valor nuevo (write-only)
  const [waKeySet, setWaKeySet] = useState(false);    // ya hay una key guardada
  const [waKeyMasked, setWaKeyMasked] = useState('');
  const [waReveal, setWaReveal] = useState(false);
  const [waSaving, setWaSaving] = useState(false);

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      const res = await api.get('/experiencia/config', { signal });
      setToken(res.data.api_token || '');
      setWaActivo(!!res.data.whatsapp_nps_activo);
      setWaUrl(res.data.whatsapp_nps_url || '');
      setWaKeySet(!!res.data.whatsapp_nps_api_key_set);
      setWaKeyMasked(res.data.whatsapp_nps_api_key_masked || '');
      setWaApiKey('');
    } catch (e) {
      if (isCanceled(e)) return;
      if (e?.response?.status === 403) {
        toast.error('No tienes permisos para ver la Configuración CX');
      } else {
        toast.error('Error al cargar la configuración CX');
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  useAbortableLoad((signal) => load(signal), [load]);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copiado`),
      () => toast.error('No se pudo copiar')
    );
  };

  const regenerar = async () => {
    if (!window.confirm('¿Regenerar la API Key? Las plataformas que usen la API Key actual dejarán de funcionar hasta actualizarla.')) return;
    setRegenerating(true);
    try {
      const res = await api.post('/experiencia/config/regenerar-token');
      setToken(res.data.api_token);
      setReveal(true);
      toast.success('API Key regenerada');
    } catch (e) {
      toast.error('Error al regenerar la API Key');
    } finally {
      setRegenerating(false);
    }
  };

  const tokenMasked = token ? `${token.slice(0, 6)}${'•'.repeat(Math.max(token.length - 10, 6))}${token.slice(-4)}` : '';

  const saveWhatsApp = async () => {
    if (waActivo && !waUrl.trim()) { toast.error('Ingresa la URL de la API de WhatsApp'); return; }
    if (waActivo && !waKeySet && !waApiKey.trim()) { toast.error('Ingresa la API Key de WhatsApp'); return; }
    setWaSaving(true);
    try {
      const payload = { whatsapp_nps_activo: waActivo, whatsapp_nps_url: waUrl.trim() };
      if (waApiKey.trim()) payload.whatsapp_nps_api_key = waApiKey.trim();
      const res = await api.put('/experiencia/config/whatsapp', payload);
      setWaKeySet(!!res.data.whatsapp_nps_api_key_set);
      setWaKeyMasked(res.data.whatsapp_nps_api_key_masked || '');
      setWaApiKey('');
      setWaReveal(false);
      toast.success('Configuración de NPS por WhatsApp guardada');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar la configuración de WhatsApp');
    } finally {
      setWaSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-5 animate-fadeIn" data-testid="cx-config-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Settings className="w-6 h-6" /> Configuración CX
        </h1>
        <p className="text-slate-500 text-sm mt-1">API de Ingesta de Encuestas (NPS/CSAT) e integración con plataformas externas.</p>
      </div>

      {loading ? (
        <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <>
          {/* URL de la API de Ingesta */}
          <Card className="border-slate-200">
            <CardContent className="p-5 lg:p-6 space-y-3">
              <Label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Plug className="w-4 h-4" /> URL de la API de Ingesta</Label>
              <div className="flex gap-2">
                <Input readOnly value={API_URL} className="font-mono text-xs" data-testid="api-url-input" />
                <Button variant="outline" size="icon" onClick={() => copy(API_URL, 'URL')} data-testid="copy-url-btn" title="Copiar URL">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-400">Método <span className="font-mono">POST</span> · Content-Type <span className="font-mono">application/json</span></p>
            </CardContent>
          </Card>

          {/* API Key */}
          <Card className="border-slate-200">
            <CardContent className="p-5 lg:p-6 space-y-3">
              <Label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><KeyRound className="w-4 h-4" /> API Key</Label>
              <p className="text-xs text-slate-500">Se envía en el header <span className="font-mono">Authorization: Bearer &lt;api_key&gt;</span>. Manténla en secreto.</p>
              <div className="flex gap-2">
                <Input readOnly value={reveal ? token : tokenMasked} className="font-mono text-xs" data-testid="api-key-input" />
                <Button variant="outline" size="icon" onClick={() => setReveal((v) => !v)} data-testid="toggle-token-btn" title={reveal ? 'Ocultar' : 'Mostrar'}>
                  {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <Button variant="outline" size="icon" onClick={() => copy(token, 'API Key')} data-testid="copy-token-btn" title="Copiar API Key">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={regenerar} disabled={regenerating} data-testid="regenerar-token-btn" className="text-red-600 hover:text-red-700">
                  {regenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Regenerar API Key
                </Button>
                <Button size="sm" className="text-white" onClick={() => setGuideOpen(true)} data-testid="ver-guia-btn" {...getPrimaryButtonStyles(themeColors)}>
                  <BookOpen className="w-4 h-4 mr-2" /> Ver guía de integración
                </Button>
              </div>
              <Alert className="bg-amber-50 border-amber-200 mt-2">
                <ShieldAlert className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 text-xs">
                  Al regenerar la API Key, las plataformas que usen la anterior dejarán de enviar encuestas hasta que la actualices.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Encuesta NPS por WhatsApp */}
          <Card className="border-slate-200" data-testid="cx-whatsapp-card">
            <CardContent className="p-5 lg:p-6 space-y-4">
              <Label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <MessageCircle className="w-4 h-4" /> Encuesta NPS por WhatsApp
              </Label>

              <div className="flex items-start justify-between gap-4 p-3 rounded-md bg-slate-50 border border-slate-200">
                <div className="space-y-1 pr-2">
                  <p className="text-sm font-medium text-slate-800">Activar envío automático de NPS por WhatsApp</p>
                  <p className="text-xs text-slate-500 max-w-2xl">
                    Al activar esta opción, cada vez que coloques un ticket en estado Terminado, se enviará
                    automáticamente la encuesta NPS al cliente relacionado.
                  </p>
                </div>
                <Switch checked={waActivo} onCheckedChange={setWaActivo} data-testid="whatsapp-nps-switch" />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-600">URL de la API de envío de NPS por WhatsApp</Label>
                <Input
                  value={waUrl}
                  onChange={(e) => setWaUrl(e.target.value)}
                  placeholder="https://..."
                  className="font-mono text-xs"
                  data-testid="whatsapp-url-input"
                />
                <p className="text-xs text-slate-400">Método <span className="font-mono">POST</span> · Content-Type <span className="font-mono">application/json</span></p>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-600">API Key</Label>
                <p className="text-xs text-slate-500">Se envía en el header <span className="font-mono">Authorization: Bearer &lt;api_key&gt;</span>. Manténla en secreto.</p>
                <div className="flex gap-2">
                  <Input
                    type={waReveal ? 'text' : 'password'}
                    value={waApiKey}
                    onChange={(e) => setWaApiKey(e.target.value)}
                    placeholder={waKeySet ? `Guardada (${waKeyMasked || '••••'}) — escribe para reemplazar` : 'Pega aquí la API Key'}
                    className="font-mono text-xs"
                    data-testid="whatsapp-apikey-input"
                  />
                  <Button variant="outline" size="icon" onClick={() => setWaReveal((v) => !v)} data-testid="whatsapp-toggle-key-btn" title={waReveal ? 'Ocultar' : 'Mostrar'}>
                    {waReveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => copy(waApiKey, 'API Key')} disabled={!waApiKey} data-testid="whatsapp-copy-key-btn" title="Copiar API Key">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="flex justify-end pt-1">
                <Button size="sm" className="text-white" onClick={saveWhatsApp} disabled={waSaving} data-testid="whatsapp-save-btn" {...getPrimaryButtonStyles(themeColors)}>
                  {waSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Guía de integración */}
      <Dialog open={guideOpen} onOpenChange={setGuideOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="guia-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5" /> Guía: API de Ingesta de Encuestas (NPS/CSAT)</DialogTitle>
          </DialogHeader>
          <div style={{ marginBottom: 8 }}>
            <Button variant="outline" size="sm" data-testid="guia-encuestas-md-btn"
              onClick={() => {
                const blob = new Blob([construirMdEncuestas(API_URL)], { type: 'text/markdown;charset=utf-8' });
                const u = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = u; a.download = 'ingesta-encuestas.md';
                document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
              }}>
              <Download className="w-4 h-4 mr-2" /> Descargar .md
            </Button>
          </div>
          <div className="space-y-4 text-sm text-slate-700">
            <div>
              <h3 className="font-semibold mb-1">Endpoint</h3>
              <pre className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs overflow-x-auto"><code>POST {API_URL}</code></pre>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Autenticación</h3>
              <pre className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs overflow-x-auto"><code>Authorization: Bearer &lt;api_key&gt;
Content-Type: application/json</code></pre>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Cuerpo (JSON)</h3>
              <ul className="list-disc pl-5 space-y-1 text-xs">
                <li><span className="font-mono">codigo_canal</span> — <b>obligatorio</b>. Canal de la interacción: <span className="font-mono">"TELEFONO"</span>, <span className="font-mono">"WHATSAPP"</span> u otro código de canal registrado.</li>
                <li><span className="font-mono">codigo_punto_contacto</span> — <b>opcional</b>. Si se omite, se deduce del <span className="font-mono">codigo_canal</span>.</li>
                <li><span className="font-mono">codigo_ticket</span> — <b>opcional**</b>. N° de ticket (ej. <span className="font-mono">TCK-2026-00049</span>); vincula la respuesta al ticket.</li>
                <li><span className="font-mono">codigo_proyecto</span> — <b>opcional</b>. Si se envía, se valida contra el catálogo de proyectos.</li>
                <li><span className="font-mono">documento</span> / <span className="font-mono">email</span> / <span className="font-mono">celular</span> — <b>opcional**</b>. Identificadores del cliente.</li>
                <li><span className="font-mono">nps</span> — <b>opcional*</b>. Entero 0–10.</li>
                <li><span className="font-mono">csat</span> — <b>opcional*</b>. Entero 0–5.</li>
                <li><span className="font-mono">comentario</span> — <b>opcional</b>.</li>
                <li><span className="font-mono">area_funcional</span> — <b>opcional</b>.</li>
                <li><span className="font-mono">codigo_etapa</span> — <b>opcional</b>. Requiere <span className="font-mono">codigo_proyecto</span> y pertenecer a ese proyecto.</li>
                <li><span className="font-mono">fecha</span> (ISO) — <b>opcional</b>.</li>
              </ul>
              <div className="mt-2 space-y-0.5 text-[11px] text-slate-500">
                <p><b>*</b> Debe enviarse al menos uno de <span className="font-mono">nps</span> o <span className="font-mono">csat</span>.</p>
                <p><b>**</b> Regla de identificación: debe enviarse <span className="font-mono">codigo_ticket</span> <b>o</b> al menos un identificador (<span className="font-mono">documento</span> / <span className="font-mono">email</span> / <span className="font-mono">celular</span>). Si va solo <span className="font-mono">codigo_ticket</span>, el cliente se resuelve desde el ticket (Cliente 360).</p>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Ejemplo A — por identificador (cURL)</h3>
              <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-xs overflow-x-auto"><code>{`curl -X POST "${API_URL}" \\
  -H "Authorization: Bearer ${reveal ? token : '<api_key>'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "codigo_canal": "WHATSAPP",
    "codigo_punto_contacto": "SAC_TICKETS",
    "codigo_ticket": "TCK-2026-00049",
    "codigo_proyecto": "PAA",
    "documento": "72738721",
    "email": "cliente@dominio.com",
    "celular": "+51987654321",
    "nps": 9,
    "csat": 4,
    "comentario": "Atención rápida",
    "area_funcional": "Postventa"
  }'`}</code></pre>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Ejemplo B — canal TELEFONO, solo por codigo_ticket (cURL)</h3>
              <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-xs overflow-x-auto"><code>{`curl -X POST "${API_URL}" \\
  -H "Authorization: Bearer ${reveal ? token : '<api_key>'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "codigo_canal": "TELEFONO",
    "codigo_ticket": "TCK-2026-00049",
    "nps": 10,
    "csat": 5,
    "comentario": "Muy satisfecha con la atención"
  }'`}</code></pre>
              <p className="text-xs text-slate-500 mt-1">Sin identificadores: el cliente se toma del ticket y el punto de contacto se deduce del canal.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Respuesta (200)</h3>
              <pre className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs overflow-x-auto"><code>{`{
  "ok": true,
  "respuesta_id": "uuid",
  "cliente_id": "uuid | null",         // cuando se ingresa por codigo_ticket, se completa con el cliente del ticket
  "punto_contacto_resuelto": true,      // false si el código de punto no se pudo resolver
  "match": "actualizada" | "insertada"  // 'actualizada' = hizo match con encuesta enviada por WhatsApp
}`}</code></pre>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Errores (400 / 401)</h3>
              <ul className="list-disc pl-5 space-y-1 text-xs">
                <li><span className="font-mono">401</span> — Falta la API Key o la API Key es inválida.</li>
                <li><span className="font-mono">400</span> — Falta <span className="font-mono">nps</span> y <span className="font-mono">csat</span> (debe enviar al menos uno).</li>
                <li><span className="font-mono">400</span> — <span className="font-mono">nps</span> fuera del rango 0–10.</li>
                <li><span className="font-mono">400</span> — <span className="font-mono">csat</span> fuera del rango 0–5.</li>
                <li><span className="font-mono">400</span> — Debe enviar <span className="font-mono">codigo_ticket</span> o al menos un identificador (<span className="font-mono">documento</span> / <span className="font-mono">email</span> / <span className="font-mono">celular</span>).</li>
                <li><span className="font-mono">400</span> — <span className="font-mono">codigo_proyecto</span> (si se envía) inexistente en el catálogo.</li>
                <li><span className="font-mono">400</span> — <span className="font-mono">codigo_etapa</span> inválida, sin <span className="font-mono">codigo_proyecto</span>, o que no pertenece al proyecto indicado.</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Semáforo</h3>
              <p className="text-xs">NPS/CSAT general = encuesta más reciente. Verde 9–10 (Promotor) · Ámbar 7–8 (Pasivo) · Rojo 0–6 (Detractor). Cliente Crítico = ocupación influyente + NPS detractor.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CXConfiguracionPage;
